import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    addFCTransferLineItem,
    cancelFCTransfer,
    createFCTransfer,
    getFCTransferById,
    mapTransferIdToApiPathParam,
    removeFCTransferLineItem,
    searchAvailableTransferLots,
    searchFCTransfers,
} from 'api/warehouse/fcTransfers';

/**
 * @param {unknown} facilityId
 * @returns {number | null}
 */
function normalizePositiveFacilityId(facilityId) {
    if (facilityId === undefined || facilityId === null || facilityId === '') {
        return null;
    }
    const n = Number(facilityId);
    if (!Number.isFinite(n) || n <= 0) {
        return null;
    }
    return n;
}

export const fcTransferQueryKeys = {
    all: ['fcTransfers'],
    searchPaginated: (params) => ['fcTransfers', 'v1', 'search', 'paginated', params],
    /** @param {string | number | undefined | null} transferId */
    detail: (transferId) => ['fcTransfers', 'v1', 'detail', transferId],
    /** Eligible transfer lots for a source facility (create / edit lot picker). */
    availableLots: (facilityId) => ['fcTransfers', 'v1', 'transferInventory', facilityId],
};

/**
 * Single-page (cursor) FC Transfers list search; mirrors `usePackagingPurchaseOrdersV2Search`.
 *
 * @param {Object} [params={}] - Merged list filters + pagination + sort
 * @param {Object} [options={}] - React Query options (`enabled`, etc.)
 * @returns {import('@tanstack/react-query').UseQueryResult<{rows: Array, pagination: {hasMore?: boolean, nextCursor?: *, totalCount?: number}}, Error>}
 */
export function useFCTransfersSearch(params = {}, options = {}) {
    return useQuery({
        queryKey: fcTransferQueryKeys.searchPaginated(params),
        queryFn: async ({ signal }) => {
            const response = await searchFCTransfers(params, { signal });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch FC transfers');
            }
            return response.data;
        },
        staleTime: 30 * 1000,
        ...options,
    });
}

/**
 * FC transfer entity (header + items); response shape matches read API `data.transfer`.
 *
 * @param {Object} [args={}]
 * @param {boolean} [args.enabled=true] - Passed through to `useQuery` (default true; combine with valid id in callers)
 * @param {string | number | undefined | null} [args.transferId] - Transfer id from route or selection
 * @param {Omit<import('@tanstack/react-query').UseQueryOptions<Object, Error, Object, readonly unknown[]>, 'queryKey' | 'queryFn'>} [queryOptions={}] - Additional React Query options
 * @returns {import('@tanstack/react-query').UseQueryResult<Object, Error>}
 */
export function useFCTransferById({ enabled = true, transferId, ...queryOptions } = {}) {
    const normalizedId = mapTransferIdToApiPathParam(transferId);
    return useQuery({
        queryKey: fcTransferQueryKeys.detail(normalizedId ?? transferId ?? null),
        queryFn: async ({ signal }) => {
            const response = await getFCTransferById(transferId, { signal });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch FC transfer');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        enabled: Boolean(normalizedId) && enabled,
        staleTime: 30 * 1000,
        ...queryOptions,
    });
}

/**
 * Eligible lots/locations for FC transfer line-item assembly at `facilityId`.
 *
 * @param {Object} [args={}]
 * @param {boolean} [args.enabled=true]
 * @param {number | string | undefined | null} [args.facilityId] - Source facility id
 * @param {Omit<import('@tanstack/react-query').UseQueryOptions<Array, Error, Array, readonly unknown[]>, 'queryKey' | 'queryFn'>} [queryOptions={}]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array, Error>}
 */
export function useAvailableTransferLots({ enabled = true, facilityId, ...queryOptions } = {}) {
    const id = normalizePositiveFacilityId(facilityId);

    return useQuery({
        queryKey: fcTransferQueryKeys.availableLots(id ?? facilityId ?? null),
        queryFn: async ({ signal }) => {
            const response = await searchAvailableTransferLots({ facilityId: id }, { signal });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch transfer inventory');
            }
            const rows = response?.data?.transferInventory;
            if (!Array.isArray(rows)) {
                throw new Error('Transfer inventory response missing transferInventory array');
            }
            return rows;
        },
        enabled: Boolean(id) && enabled,
        staleTime: 30 * 1000,
        ...queryOptions,
    });
}

/**
 * Creates a pending FC transfer; on success invalidates all `fcTransfers` queries so the list refetches.
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<Object, Error, Object>, 'mutationFn'>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, Object>}
 */
export function useCreateFCTransfer(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async (payload) => {
            const response = await createFCTransfer(payload);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to create FC transfer');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}

/**
 * Adds a line item to a pending FC transfer; on success invalidates FC transfer queries.
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<Object, Error, { item: Object, transferId: string | number }>, 'mutationFn'>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, { item: Object, transferId: string | number }>}
 */
export function useAddFCTransferLineItem(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ item, transferId }) => {
            const response = await addFCTransferLineItem(transferId, item);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to add FC transfer line item');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}

/**
 * Removes a line item from a pending FC transfer; on success invalidates FC transfer queries.
 *
 * @param {Omit<import('@tanstack/react-query').UseMutationOptions<Object, Error, { transferId: string | number, transferItemId: string | number }>, 'mutationFn'>} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<Object, Error, { transferId: string | number, transferItemId: string | number }>}
 */
export function useRemoveFCTransferLineItem(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ transferId, transferItemId }) => {
            const response = await removeFCTransferLineItem(transferId, transferItemId);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to remove FC transfer line item');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}

/**
 * Cancels a pending FC transfer; on success invalidates FC transfer queries.
 *
 * @param {import('@tanstack/react-query').UseMutationOptions<Object, Error, string | number>} [mutationOptions={}]
 */
export function useCancelFCTransfer(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async (transferId) => {
            const response = await cancelFCTransfer(transferId);
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to cancel FC transfer');
            }
            const transfer = response?.data?.transfer;
            if (transfer == null) {
                throw new Error('FC transfer response missing transfer payload');
            }
            return transfer;
        },
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
            return onSuccess?.(...args);
        },
        ...rest,
    });
}
